export type MemberUnion<T extends Readonly<Array<unknown>>> = T[number];

/**
   A replacement utility for Typescript enums. Why are enums kinda icky?
   https://www.aaron-powell.com/posts/2020-05-27-the-dangers-of-typescript-enums/

   Usage:

     type SwitchState = 'On' | 'Off'
     const SwitchState: ConstEnum<SwitchState> = {
       On: 'On',
       Off: 'Off',
     }
 */
export type ConstEnum<T extends string> = {
    readonly [K in T]: K;
};

export type Mutable<T> = {
    -readonly [K in keyof T]: T[K];
};

export type ExpandRecursively<T> = T extends object
    ? T extends infer O
        ? { [K in keyof O]: ExpandRecursively<O[K]> }
        : never
    : T;
export type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;
export type AtLeastOne<T> = [T, ...T[]];

export type RequireAtLeastOneProperty<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
    U[keyof U];

/**
 * Typechecks that a string array contains all members of a union. e.g.
 *
 *   ```ts
 *   type Fish = 'salmon' | 'trout';
 *
 *   const incompleteFish: ArrayOfUnion<Fish> = exhaustive<Fish>('salmon') // error
 *   const completeFish: ArrayOfUnion<Fish>   = exhaustive<Fish>('salmon', 'trout'); // typechecks
 *   ```
 **/
export const exhaustiveTuple =
    <T extends string>() =>
    <U extends AtLeastOne<T>>(
        ...members: U extends any
            ? Exclude<T, U[number]> extends never
                ? U
                : Exclude<T, U[number]>[]
            : never
    ) =>
        members;

export const unionToEnum =
    <T extends string>() =>
    // all this legerdemain is for ensuring that the `members` array is
    // exhaustive over the union `T`
    <U extends AtLeastOne<T>>(
        ...members: U extends any
            ? Exclude<T, U[number]> extends never
                ? U
                : Exclude<T, U[number]>[]
            : never
    ): ConstEnum<T> => {
        const mutable: Partial<Mutable<ConstEnum<T>>> = {};

        for (const value of members) {
            mutable[value] = value;
        }

        Object.freeze(mutable);

        return mutable as ConstEnum<T>;
    };

export const assertNever = (value: never): never => {
    throw new Error(`assertNever failed with value: ${JSON.stringify(value, null, 2)}`);
};

export const nonNullable = <T>(t: T): t is NonNullable<T> => t !== null && t !== undefined;

/**
 * Support type narrowing on key with `unknown` values because `'key'
 * in object && object.key === 'value' doesn't narrow until TS 4.9
 */
export const hasKey = <T, K extends string>(o: T, k: K): o is T & Record<K, unknown> =>
    typeof o === 'object' && o !== null && k in o;

export type CamelCase<S extends string> = S extends `${infer P1}_${infer P2}${infer P3}`
    ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
    : Lowercase<S>;

export type CamelKeys<T> = {
    [K in keyof T as CamelCase<string & K>]: T[K] extends Record<string, any>
        ? CamelKeys<T[K]>
        : T[K];
};

/**
 * UNSAFE but useful for checking properties that are not in an object's
 * type. Example:
 *
 *   const isPickTwo: boolean = !!getUndocumentedProperty(menuItem, 'is_pick_two');
 *
 * This is not a type safe function, use it with caution and have a
 * fallback. (note the !! to cast this value to a boolean)
 **/
export const getUndocumentedProperty = (obj: any, property: string, fallback?: any) => {
    if (property in obj) {
        return obj[property];
    }
    console.warn(`[getUndocumentedProperty] failed to get undocumented property ${property}`);
    return fallback;
};
